import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import {
  Button,
  ButtonGroup,
  Checkbox,
  DatePicker,
  DateRangePicker,
  Form,
  Input,
  Message,
  Panel,
  SelectPicker,
  Toggle,
  Tooltip,
  Whisper,
} from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import './ExpenseForm.css';
import { showToast } from '../../utils/toast';
import { formatCurrency, formatCurrencyTyping, sanitizeAmount } from '../../utils/currency';
import ExpenseAttachmentsPanel from './ExpenseAttachmentsPanel';
import { useAuth } from '../../contexts/AuthContext';
import {
  ALLOWED_ATTACHMENT_SUMMARY,
  ATTACHMENT_ACCEPT,
  partitionAllowedFiles,
  unsupportedFilesMessage,
} from '../../utils/expenseAttachments';
import {
  useCompanyDirectory,
  useExpenseAllHandsMeal,
  useExpenseAttachments,
  useExpenseCustomers,
  useExpenseMileageRates,
  useExpenseVendors,
  useUpdateExpenseAttachmentReceipt,
} from '../../hooks/useQueries';
import { ReceiptTagFields, ReceiptTotalsSummary } from './ExpenseReceiptFields';
import {
  ALL_HANDS_MEAL_CATEGORY,
  CATEGORY_OPTIONS,
  TECHNOLOGY_REIMBURSEMENT_CATEGORY,
  isAllHandsMealCategory,
  isTravelEligibleCategory,
  isTravelRequiredCategory,
  isVendorNameRequired,
  normalizeCategory,
  normalizeCategoryOrEmpty,
} from '../../utils/expenseCategories';
import {
  MILEAGE_CATEGORY,
  computeMileageAmount,
  findMileageRate,
  findMileageRateChange,
  formatIsoDate,
  formatMileageRate,
  sanitizeMiles,
} from '../../utils/mileage';

// Dollar amount reimbursed per elapsed month for technology reimbursements.
// Adjust here if the monthly rate changes.
const TECHNOLOGY_REIMBURSEMENT_MONTHLY_RATE = 50;

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, monthIndex, day);
    }
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateOnly = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatInputDate = (date) => {
  if (!date) return '';
  return formatDateOnly(date);
};

// Inclusive count of calendar months spanned by two dates (e.g. Jan -> Jun = 6).
const monthsElapsed = (start, end) => {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return 0;
  const months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12
    + (endDate.getMonth() - startDate.getMonth())
    + 1;
  return Math.max(0, months);
};

const startOfMonth = (value) => {
  const date = toDate(value);
  return date ? new Date(date.getFullYear(), date.getMonth(), 1) : null;
};

const endOfMonth = (value) => {
  const date = toDate(value);
  return date ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : null;
};

const buildInitialFormState = (initialValue) => {
  const purchaseDate = toDate(initialValue?.purchase_date) || new Date();
  const purchaseStartDate = toDate(initialValue?.purchase_start_date);
  const purchaseEndDate = toDate(initialValue?.purchase_end_date);
  const hasMultiplePurchaseDates =
    !!purchaseStartDate
    && !!purchaseEndDate
    && formatDateOnly(purchaseStartDate) !== formatDateOnly(purchaseEndDate);
  const travelStartDate = toDate(initialValue?.travel_start_date);
  const travelEndDate = toDate(initialValue?.travel_end_date);

  return {
    formValue: {
      purchase_date: purchaseDate,
      purchase_date_range: hasMultiplePurchaseDates ? [purchaseStartDate, purchaseEndDate] : null,
      has_multiple_purchase_dates: hasMultiplePurchaseDates,
      vendor_name: initialValue?.vendor_name || '',
      // Empty until the submitter picks one — the rest of the form stays hidden
      // until then, so it must not start on a default.
      category: normalizeCategoryOrEmpty(initialValue?.category),
      description: initialValue?.description || '',
      reason_for_purchase: initialValue?.reason_for_purchase || '',
      purchased_by_me: initialValue?.purchased_by_me === true,
      customer_reimbursable: initialValue?.customer_reimbursable === true,
      customer_name: initialValue?.customer_name || '',
      travel: initialValue?.travel === true || isTravelRequiredCategory(initialValue?.category),
      travel_dates: travelStartDate && travelEndDate ? [travelStartDate, travelEndDate] : null,
      mileage_miles: initialValue?.mileage_miles == null ? '' : String(Number(initialValue.mileage_miles)),
    },
    amountRaw: initialValue?.amount == null ? '' : String(initialValue.amount),
    amountDisplay: initialValue?.amount == null ? '' : formatCurrency(initialValue.amount),
  };
};

const toComparableState = ({ formValue, amountRaw }) => {
  return {
    purchase_date: formatDateOnly(toDate(formValue.purchase_date)),
    purchase_start_date: !formValue.travel && formValue.has_multiple_purchase_dates
      ? formatDateOnly(toDate(formValue.purchase_date_range?.[0]))
      : null,
    purchase_end_date: !formValue.travel && formValue.has_multiple_purchase_dates
      ? formatDateOnly(toDate(formValue.purchase_date_range?.[1]))
      : null,
    has_multiple_purchase_dates: !formValue.travel && formValue.has_multiple_purchase_dates === true,
    vendor_name: String(formValue.vendor_name || '').trim(),
    category: normalizeCategoryOrEmpty(formValue.category),
    description: String(formValue.description || '').trim(),
    reason_for_purchase: String(formValue.reason_for_purchase || '').trim(),
    purchased_by_me: formValue.purchased_by_me !== false,
    customer_reimbursable: formValue.customer_reimbursable === true,
    customer_name: formValue.customer_reimbursable ? String(formValue.customer_name || '').trim() : '',
    travel: formValue.travel === true,
    travel_start_date: formValue.travel ? formatDateOnly(toDate(formValue.travel_dates?.[0])) : null,
    travel_end_date: formValue.travel ? formatDateOnly(toDate(formValue.travel_dates?.[1])) : null,
    mileage_miles: (Number(sanitizeMiles(formValue.mileage_miles)) || 0).toFixed(2),
    // Compare the amount numerically so cosmetic formatting differences don't
    // read as a change. Technology reimbursements recompute the amount on mount
    // (e.g. "100"), while the stored value comes back formatted ("100.00"); a
    // raw string compare would flag the untouched form as dirty.
    amount: (Number(sanitizeAmount(amountRaw)) || 0).toFixed(2),
  };
};

const fileIdentity = (file) => `${file?.name || ''}::${file?.size || 0}::${file?.lastModified || 0}`;

// The checks that only apply to an expense being claimed: that a receipt is
// attached, and that the receipts add up to the amount. Saving without
// submitting skips these — see isDraftValid.
const RECEIPT_VALIDATION_FIELDS = ['attachments', 'receipt_amounts', 'receipt_total'];

const RequiredAsterisk = () => (
  <span className="text-danger ms-1" aria-hidden="true">*</span>
);

const validateRequiredFields = (formValue, amountRaw) => {
  const errors = {};
  const category = normalizeCategoryOrEmpty(formValue.category);
  const isTechReimbursement = category === TECHNOLOGY_REIMBURSEMENT_CATEGORY;
  const isMileage = category === MILEAGE_CATEGORY;

  // Mileage is priced from the miles and the trip date, so it is the miles
  // that get asked for; what can stop it being priced is reported by the
  // form, where the rates are.
  const amountValue = Number(amountRaw);
  if (!isMileage && (!Number.isFinite(amountValue) || amountValue <= 0)) {
    errors.amount = 'Amount must be greater than 0.';
  }

  if (isMileage && !(Number(sanitizeMiles(formValue.mileage_miles)) > 0)) {
    errors.mileage_miles = 'Miles driven is required.';
  }

  if (!category) {
    // Amount and category are the only fields on screen until a category is
    // picked, so don't report problems with fields nobody can see yet.
    errors.category = 'Category is required.';
    return errors;
  }

  // The All Hands meal asks for nothing but the meeting date; the server fixes
  // everything else about it.
  if (isAllHandsMealCategory(category)) {
    if (!toDate(formValue.purchase_date)) {
      errors.purchase_date = 'Meeting date is required.';
    }
    return errors;
  }

  if (formValue.travel) {
    const start = toDate(formValue.travel_dates?.[0]);
    const end = toDate(formValue.travel_dates?.[1]);
    if (!start || !end) {
      errors.travel_dates = 'Travel date range is required.';
    }
    // Travel expenses still require a purchase date alongside the travel range.
    if (!toDate(formValue.purchase_date)) {
      errors.purchase_date = 'Purchase date is required.';
    }
  } else if (formValue.has_multiple_purchase_dates) {
    const start = toDate(formValue.purchase_date_range?.[0]);
    const end = toDate(formValue.purchase_date_range?.[1]);
    if (!start || !end) {
      errors.purchase_date_range = 'Purchase date range is required.';
    } else if (end < start) {
      // Tech reimbursements pick the start/end months with independent
      // DatePickers, so an inverted range is possible; flag it explicitly here
      // rather than letting the read-only computed amount fail validation.
      errors.purchase_date_range = 'End date must be on or after the start date.';
    }
  } else if (!toDate(formValue.purchase_date)) {
    errors.purchase_date = 'Purchase date is required.';
  }

  if (isVendorNameRequired(formValue.category) && !String(formValue.vendor_name || '').trim()) {
    errors.vendor_name = 'Vendor name is required.';
  }

  if (!String(formValue.description || '').trim()) {
    errors.description = 'Description is required.';
  }

  if (!isTechReimbursement && !String(formValue.reason_for_purchase || '').trim()) {
    errors.reason_for_purchase = 'Reason for purchase is required.';
  }

  if (!isTechReimbursement && formValue.customer_reimbursable && !String(formValue.customer_name || '').trim()) {
    errors.customer_name = 'Customer is required when reimbursable is selected.';
  }

  return errors;
};

export default function ExpenseForm({
  initialValue,
  onSubmit,
  onCancel,
  onDirtyChange,
  submitRef,
  // Pins the expense to one employee, for when the choice was already made
  // elsewhere — a batch settles its owner when it is started.
  lockedEmployeeId = null,
  showActions = true,
  // Offers "save without submitting" alongside the submit action. On for a new
  // expense and for one that hasn't been sent yet; off once it is with finance,
  // where saving a change is the only thing left to do.
  showSaveAction = false,
  // Offers the pre-approved All Hands meal. Off inside a group: it is filed
  // approved on its own, so it can't wait to be submitted with a batch.
  allowAllHandsMeal = true,
  submitLabel = 'Submit Expense',
  saveLabel = 'Save for later',
  cancelLabel = 'Cancel',
  loading = false,
  error,
}) {
  const auth = useAuth();

  const vendorQuery = useExpenseVendors({ enabled: true });
  const allHandsMealQuery = useExpenseAllHandsMeal({ enabled: true });
  const allHandsMealAmount = allHandsMealQuery.data?.amount ?? null;
  const mileageRatesQuery = useExpenseMileageRates({ enabled: true });
  const customerQuery = useExpenseCustomers({ enabled: true });

  const initialFormState = useMemo(() => buildInitialFormState(initialValue), [initialValue]);
  const isEditing = Boolean(initialValue?.id);

  // Submitting on behalf of another employee is gated behind a permission and
  // only applies when creating a new expense. When allowed, the submitter can
  // pick any active employee to own the resulting expense.
  const canSubmitOnBehalf =
    !isEditing
    && typeof auth.hasPermission === 'function'
    && auth.hasPermission('submit_expenses_on_behalf');
  const directoryQuery = useCompanyDirectory({ enabled: canSubmitOnBehalf });
  const [onBehalfEmployeeId, setOnBehalfEmployeeId] = useState(null);
  const selectedEmployeeId = lockedEmployeeId || onBehalfEmployeeId || auth.employee?.id || null;

  const [formValue, setFormValue] = useState(initialFormState.formValue);

  const [amountRaw, setAmountRaw] = useState(initialFormState.amountRaw);
  const [amountDisplay, setAmountDisplay] = useState(initialFormState.amountDisplay);
  const [attachments, setAttachments] = useState([]);
  // Per-file receipt tagging, keyed by fileIdentity so `attachments` stays a
  // plain File[] for upload. Shape: { isReceipt, amountRaw, amountDisplay }.
  const [receiptMeta, setReceiptMeta] = useState({});
  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const [unsupportedFileError, setUnsupportedFileError] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  // New expenses require an explicit receipt confirmation before they are sent.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const rootRef = useRef(null);
  const travelDatesRef = useRef(null);
  const travelDatesStartRef = useRef(null);

  // RSuite's DateRangePicker only commits on OK. Track the two calendar clicks
  // ourselves so the second one applies the range and closes the panel.
  const handleTravelDateSelect = useCallback((date) => {
    const start = travelDatesStartRef.current;
    if (!start) {
      travelDatesStartRef.current = date;
      return;
    }
    travelDatesStartRef.current = null;
    const range = start <= date ? [start, date] : [date, start];
    setFormValue((prev) => ({ ...prev, travel_dates: range }));
    travelDatesRef.current?.close();
  }, []);

  const resetTravelDateSelection = useCallback(() => {
    travelDatesStartRef.current = null;
  }, []);

  // RSuite pickers portal their overlay to document.body by default, so the
  // panel does not follow the modal's internal scroll and drifts away from its
  // toggle. Rendering the overlay inside the scrollable .nsi-modal__body keeps the
  // panel anchored to the toggle as the modal scrolls.
  const getPickerContainer = useCallback(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    return node.closest('.nsi-modal__body') || node.parentElement || undefined;
  }, []);

  useEffect(() => {
    setFormValue(initialFormState.formValue);
    setAmountRaw(initialFormState.amountRaw);
    setAmountDisplay(initialFormState.amountDisplay);
    setAttachments([]);
    setReceiptMeta({});
    setIsDragOverUpload(false);
    setUnsupportedFileError('');
    setValidationAttempted(false);
    setConfirmOpen(false);
    setReceiptConfirmed(false);
  }, [initialFormState]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateMatch = (eventOrList) => {
      setIsMobileViewport(Boolean(eventOrList.matches));
    };

    updateMatch(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateMatch);
      return () => mediaQuery.removeEventListener('change', updateMatch);
    }

    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  // Each attachment paired with its receipt tagging. Not every file is a
  // receipt (a mileage route screenshot isn't), so the tagging is per file.
  //
  // Which files those are depends on the mode: a new expense holds them locally
  // until it exists, while an edit reads the ones already stored against it.
  // Everything downstream — the totals, the validation, the controls under each
  // file — works off these entries, so both modes behave identically.
  const storedAttachmentsQuery = useExpenseAttachments(initialValue?.id, { enabled: isEditing });
  const updateAttachmentReceipt = useUpdateExpenseAttachmentReceipt();

  // Receipt tagging is written as it changes, with nothing on screen tied to
  // the request. Say so when one fails, or the form would go on showing an
  // answer the expense didn't keep.
  useEffect(() => {
    if (!updateAttachmentReceipt.error) return;
    showToast('error', String(updateAttachmentReceipt.error?.message || updateAttachmentReceipt.error), {
      header: "Couldn't save receipt details",
      duration: 7000,
    });
  }, [updateAttachmentReceipt.error]);
  const storedAttachments = useMemo(
    () => storedAttachmentsQuery.data || [],
    [storedAttachmentsQuery.data]
  );

  const attachmentEntries = useMemo(
    () => {
      const files = isEditing
        ? storedAttachments.map((row) => ({ key: String(row.id), name: row.file_name, file: null, stored: row }))
        : (attachments || []).map((file) => ({ key: fileIdentity(file), name: file?.name, file, stored: null }));

      return files.map((entry) => {
        // What's stored on the attachment is the starting answer; a local edit
        // takes over from the moment it's made, so typing an amount isn't
        // fighting a refetch of the row it's being typed into.
        const meta = receiptMeta[entry.key];
        if (!meta) {
          const storedAmount = entry.stored?.receipt_amount;
          const hasAmount = storedAmount != null && storedAmount !== '';
          return {
            ...entry,
            isReceipt: entry.stored?.is_receipt === true,
            amountRaw: hasAmount ? String(Number(storedAmount)) : '',
            amountDisplay: hasAmount ? formatCurrency(Number(storedAmount)) : '',
          };
        }

        return {
          ...entry,
          isReceipt: meta.isReceipt === true,
          amountRaw: meta.amountRaw || '',
          amountDisplay: meta.amountDisplay || '',
        };
      });
    },
    [attachments, isEditing, receiptMeta, storedAttachments]
  );

  const receiptEntries = useMemo(
    () => attachmentEntries.filter((entry) => entry.isReceipt),
    [attachmentEntries]
  );
  const receiptTotal = useMemo(
    () => receiptEntries.reduce((sum, entry) => sum + (Number(sanitizeAmount(entry.amountRaw)) || 0), 0),
    [receiptEntries]
  );

  // Mileage is miles times the rate in force on the trip date: the purchase
  // date, or the purchase date range when there is one. The server reprices on
  // save; this is so the figure, and anything that would stop it being priced,
  // is on screen as it's typed.
  const mileagePricing = useMemo(() => {
    if (normalizeCategoryOrEmpty(formValue.category) !== MILEAGE_CATEGORY) return null;

    const usesRange = !formValue.travel && formValue.has_multiple_purchase_dates;
    const startIso = formatDateOnly(toDate(usesRange ? formValue.purchase_date_range?.[0] : formValue.purchase_date));
    const endIso = formatDateOnly(toDate(usesRange ? formValue.purchase_date_range?.[1] : formValue.purchase_date));
    const rates = mileageRatesQuery.data || [];
    const rate = findMileageRate(rates, startIso);
    const change = findMileageRateChange(rates, startIso, endIso);
    const miles = Number(sanitizeMiles(formValue.mileage_miles)) || 0;

    let error = null;
    if (mileageRatesQuery.isError) {
      error = "Couldn't load the mileage rates. Close the form and try again.";
    } else if (mileageRatesQuery.isLoading) {
      error = 'Mileage rates are still loading.';
    } else if (!startIso) {
      // No date yet: the date field reports that itself.
    } else if (!rate) {
      error = `No mileage rate is set for ${formatIsoDate(startIso)}. Ask finance to add one.`;
    } else if (change) {
      error = `The mileage rate changed on ${formatIsoDate(change.effective_date)}. `
        + 'Split the trips before and after that date into separate expenses.';
    }

    const amount = !error && rate && miles > 0 ? computeMileageAmount(miles, rate.rate_per_mile) : null;
    return { rate: error ? null : rate, miles, amount, error };
  }, [formValue, mileageRatesQuery.data, mileageRatesQuery.isError, mileageRatesQuery.isLoading]);

  const validationErrors = useMemo(
    () => {
      const errors = validateRequiredFields(formValue, amountRaw);
      if (mileagePricing?.error) errors.mileage_rate = mileagePricing.error;
      // The uploader is part of the form that stays hidden until a category is
      // picked, so there is nothing to ask for until then.
      const category = normalizeCategoryOrEmpty(formValue.category);
      // The All Hands meal is pre-approved and needs no receipt.
      if (!category || isAllHandsMealCategory(category)) return errors;

      // Every expense except technology reimbursements must have a receipt
      // attached before it can be submitted. An edit isn't blocked on it: the
      // expense already exists, files there are added straight to it rather
      // than on save, and expenses predating this rule would be uneditable.
      const isTech = category === TECHNOLOGY_REIMBURSEMENT_CATEGORY;
      if (!isEditing && !isTech && (attachments || []).length === 0) {
        errors.attachments = 'A receipt attachment is required.';
      }

      // The totals check applies wherever files are tagged, edits included —
      // it only bites once something has been marked as a receipt, so it asks
      // nothing of an edit that doesn't touch them.
      if (!isTech && receiptEntries.length > 0) {
        if (receiptEntries.some((entry) => !(Number(sanitizeAmount(entry.amountRaw)) > 0))) {
          errors.receipt_amounts = 'Enter an amount for every attachment marked as a receipt.';
        } else {
          // Compare in cents so floating point addition can't fail an
          // otherwise exact match.
          const requestedCents = Math.round((Number(amountRaw) || 0) * 100);
          const receiptCents = Math.round(receiptTotal * 100);
          if (requestedCents !== receiptCents) {
            errors.receipt_total =
              `Receipt amounts total ${formatCurrency(receiptTotal)}, which does not match the ${formatCurrency(Number(amountRaw) || 0)} requested.`;
          }
        }
      }

      return errors;
    },
    [formValue, amountRaw, attachments, isEditing, receiptEntries, receiptTotal, mileagePricing]
  );
  const receiptTotalMatches = !validationErrors.receipt_amounts && !validationErrors.receipt_total;
  // One element for both modes, so the tally reads the same under a list of
  // pending files as it does under a list of stored ones.
  const receiptTotals = (
    <ReceiptTotalsSummary
      receiptCount={receiptEntries.length}
      receiptTotal={receiptTotal}
      requestedAmount={Number(amountRaw) || 0}
      matches={receiptTotalMatches}
      message={
        validationErrors.receipt_amounts
        || validationErrors.receipt_total
        || 'Receipt amounts match the amount requested.'
      }
    />
  );
  const isFormValid = Object.keys(validationErrors).length === 0;
  // The receipt rules are the difference between saving and submitting: a draft
  // is allowed to be waiting on its receipt, so only the fields the expense
  // can't be stored without are required to save it.
  const isDraftValid = useMemo(
    () => Object.keys(validationErrors)
      .filter((field) => !RECEIPT_VALIDATION_FIELDS.includes(field))
      .length === 0,
    [validationErrors]
  );
  const showFieldError = (field) => validationAttempted && Boolean(validationErrors[field]);
  const validationBorder = (field) => (showFieldError(field) ? { borderColor: 'var(--bs-danger)', borderWidth: 1 } : undefined);

  const selectedCategory = normalizeCategoryOrEmpty(formValue.category);
  // Everything except the amount and the category picker itself stays hidden
  // until a category is chosen: the rest of the form depends on it.
  const hasCategory = Boolean(selectedCategory);
  const isTechReimbursement = selectedCategory === TECHNOLOGY_REIMBURSEMENT_CATEGORY;
  const isMileage = selectedCategory === MILEAGE_CATEGORY;
  const isAllHandsMeal = selectedCategory === ALL_HANDS_MEAL_CATEGORY;

  // Offered on a new expense outside a group. An edit only keeps it when the
  // expense already is one, so the picker can still show its value; the
  // server refuses turning any other expense into one.
  const categoryOptions = useMemo(
    () => CATEGORY_OPTIONS.filter((option) =>
      option.value !== ALL_HANDS_MEAL_CATEGORY
      || (allowAllHandsMeal && !isEditing)
      || option.value === initialFormState.formValue.category),
    [allowAllHandsMeal, isEditing, initialFormState]
  );

  // Technology reimbursements are calculated, not entered: the amount is the
  // number of elapsed months in the purchase date range times the monthly rate.
  const techReimbursementMonths = useMemo(
    () => monthsElapsed(formValue.purchase_date_range?.[0], formValue.purchase_date_range?.[1]),
    [formValue.purchase_date_range]
  );

  useEffect(() => {
    if (!isTechReimbursement) return;
    const computed = techReimbursementMonths * TECHNOLOGY_REIMBURSEMENT_MONTHLY_RATE;
    setAmountRaw(String(computed));
    setAmountDisplay(computed > 0 ? formatCurrency(computed) : '');
  }, [isTechReimbursement, techReimbursementMonths]);

  // The All Hands meal is a fixed, server-owned amount. Until it has loaded the
  // amount is left empty, so nothing typed before the category was picked is
  // shown as if it were the pre-approved figure.
  useEffect(() => {
    if (!isAllHandsMeal) return;
    setAmountRaw(allHandsMealAmount == null ? '' : String(allHandsMealAmount));
    setAmountDisplay(allHandsMealAmount == null ? '' : formatCurrency(allHandsMealAmount));
  }, [isAllHandsMeal, allHandsMealAmount]);

  // Mileage is priced from the miles and the trip date. Until it can be (no
  // miles yet, or no rate for the date) the amount is left empty rather than
  // showing whatever was typed before the category was picked.
  const mileageAmount = mileagePricing?.amount ?? null;
  useEffect(() => {
    if (!isMileage) return;
    setAmountRaw(mileageAmount == null ? '' : String(mileageAmount));
    setAmountDisplay(mileageAmount == null ? '' : formatCurrency(mileageAmount));
  }, [isMileage, mileageAmount]);

  const initialComparable = useMemo(
    () => JSON.stringify({
      ...toComparableState(initialFormState),
      attachments: [],
    }),
    [initialFormState]
  );
  const currentComparable = useMemo(
    () => JSON.stringify({
      ...toComparableState({ formValue, amountRaw }),
      attachments: attachments.map(fileIdentity).sort(),
    }),
    [formValue, amountRaw, attachments]
  );

  useEffect(() => {
    onDirtyChange?.(currentComparable !== initialComparable);
  }, [currentComparable, initialComparable, onDirtyChange]);


  const employeeName = useMemo(() => {
    const first = auth.employee?.first_name || '';
    const last = auth.employee?.last_name || '';
    return `${first} ${last}`.trim() || auth.employee?.email || '';
  }, [auth.employee]);

  const submissionDate = useMemo(() => {
    const date = initialValue?.submission_date ? toDate(initialValue.submission_date) : new Date();
    return formatInputDate(date);
  }, [initialValue]);

  const vendorOptions = useMemo(
    () => (vendorQuery.data || []).map((value) => ({ label: value, value })),
    [vendorQuery.data]
  );

  const customerOptions = useMemo(
    () => (customerQuery.data || []).map((value) => ({ label: value, value })),
    [customerQuery.data]
  );

  const employeeOptions = useMemo(
    () => (directoryQuery.data || []).map((emp) => ({
      label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown',
      value: emp.id,
    })),
    [directoryQuery.data]
  );

  // True when an on-behalf submitter has picked an employee other than
  // themselves, so we can warn that the expense will be owned by someone else.
  const isOnBehalfOfOther = Boolean(
    canSubmitOnBehalf
    && selectedEmployeeId
    && auth.employee?.id
    && String(selectedEmployeeId) !== String(auth.employee.id)
  );
  const selectedEmployeeName = useMemo(
    () => employeeOptions.find((option) => option.value === selectedEmployeeId)?.label || '',
    [employeeOptions, selectedEmployeeId]
  );

  const selectedVendor = formValue.vendor_name ? { label: formValue.vendor_name, value: formValue.vendor_name } : null;
  const selectedCustomer = formValue.customer_name ? { label: formValue.customer_name, value: formValue.customer_name } : null;
  const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;
  const creatableSelectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  const handleAmountChange = (value) => {
    const sanitized = sanitizeAmount(value);
    setAmountRaw(sanitized);
    setAmountDisplay(formatCurrencyTyping(sanitized));
  };

  const handleAmountBlur = () => {
    if (!amountRaw) {
      setAmountDisplay('');
      return;
    }
    const numeric = Number(amountRaw);
    if (!Number.isFinite(numeric)) {
      setAmountDisplay(formatCurrencyTyping(amountRaw));
      return;
    }
    setAmountRaw(String(numeric));
    setAmountDisplay(formatCurrency(numeric));
  };

  const addFiles = useCallback((fileList) => {
    // A drop can mix supported and unsupported files; keep the good ones and
    // report the rest rather than discarding the whole batch.
    const { allowed: incoming, rejected } = partitionAllowedFiles(fileList);
    setUnsupportedFileError(unsupportedFilesMessage(rejected));
    if (incoming.length === 0) return;

    setAttachments((prev) => {
      const seen = new Set(prev.map(fileIdentity));
      const merged = [...prev];
      incoming.forEach((file) => {
        const key = fileIdentity(file);
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(file);
        }
      });
      return merged;
    });
  }, []);

  const removeAttachment = useCallback((index) => {
    const removed = attachments[index];
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    if (!removed) return;
    const key = fileIdentity(removed);
    setReceiptMeta((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  }, [attachments]);

  const setAttachmentIsReceipt = useCallback((key, isReceipt) => {
    setReceiptMeta((prev) => ({
      ...prev,
      // Clearing the amount alongside the toggle keeps a stale figure from
      // silently counting again if the file is re-marked as a receipt.
      [key]: { isReceipt, amountRaw: '', amountDisplay: '' },
    }));
  }, []);

  const setAttachmentAmount = useCallback((key, value) => {
    const sanitized = sanitizeAmount(value);
    setReceiptMeta((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { isReceipt: true }),
        amountRaw: sanitized,
        amountDisplay: formatCurrencyTyping(sanitized),
      },
    }));
  }, []);

  const formatAttachmentAmount = useCallback((key) => {
    setReceiptMeta((prev) => {
      const entry = prev[key];
      if (!entry?.amountRaw) return prev;
      const numeric = Number(entry.amountRaw);
      if (!Number.isFinite(numeric)) return prev;
      return {
        ...prev,
        [key]: { ...entry, amountRaw: String(numeric), amountDisplay: formatCurrency(numeric) },
      };
    });
  }, []);

  // On an existing expense the tagging is stored on the attachment row, so it
  // is written as it changes — the same way adding and deleting files there
  // takes effect immediately rather than on save. A new expense has no row to
  // write to yet; its tagging rides along with the upload instead.
  const persistReceiptTagging = useCallback((key, { isReceipt, amountRaw }) => {
    if (!isEditing || !initialValue?.id) return;

    const amount = Number(sanitizeAmount(amountRaw));
    updateAttachmentReceipt.mutate({
      expenseId: initialValue.id,
      attachmentId: key,
      isReceipt,
      receiptAmount: isReceipt && Number.isFinite(amount) && amount > 0 ? amount : null,
    });
  }, [isEditing, initialValue?.id, updateAttachmentReceipt]);

  const openFilePicker = useCallback(() => {
    if (loading) return;
    fileInputRef.current?.click();
  }, [loading]);

  const openCameraPicker = useCallback(() => {
    if (loading) return;
    cameraInputRef.current?.click();
  }, [loading]);

  const onUploadInputChange = useCallback((event) => {
    addFiles(event.target.files);
    event.target.value = '';
  }, [addFiles]);

  const onCameraInputChange = useCallback((event) => {
    addFiles(event.target.files);
    event.target.value = '';
  }, [addFiles]);

  const onUploadDragOver = useCallback((event) => {
    event.preventDefault();
    if (loading) return;
    setIsDragOverUpload(true);
  }, [loading]);

  const onUploadDragLeave = useCallback((event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOverUpload(false);
    }
  }, []);

  const onUploadDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragOverUpload(false);
    if (loading) return;
    addFiles(event.dataTransfer?.files);
  }, [addFiles, loading]);

  const performSubmit = useCallback(async (asDraft = false) => {
    const amountValue = Number(amountRaw);
    // Travel-required categories are always travel expenses regardless of the
    // (locked) toggle's stored state, so treat travel as on for them here.
    const travelFlag = formValue.travel || isTravelRequiredCategory(formValue.category);
    const payload = {
      ...(canSubmitOnBehalf ? { employee_id: selectedEmployeeId } : {}),
      purchase_date: formatDateOnly(formValue.purchase_date),
      purchase_start_date: !travelFlag && formValue.has_multiple_purchase_dates
        ? formatDateOnly(toDate(formValue.purchase_date_range?.[0]))
        : null,
      purchase_end_date: !travelFlag && formValue.has_multiple_purchase_dates
        ? formatDateOnly(toDate(formValue.purchase_date_range?.[1]))
        : null,
      has_multiple_purchase_dates: !travelFlag && formValue.has_multiple_purchase_dates === true,
      vendor_name: formValue.vendor_name,
      category: normalizeCategory(formValue.category),
      description: formValue.description,
      reason_for_purchase: formValue.reason_for_purchase,
      purchased_by_me: formValue.purchased_by_me,
      customer_reimbursable: formValue.customer_reimbursable,
      customer_name: formValue.customer_reimbursable ? (formValue.customer_name || null) : null,
      travel: travelFlag,
      travel_start_date: travelFlag ? formatDateOnly(formValue.travel_dates?.[0]) : null,
      travel_end_date: travelFlag ? formatDateOnly(formValue.travel_dates?.[1]) : null,
      amount: Number.isFinite(amountValue) ? amountValue : null,
      mileage_miles: normalizeCategory(formValue.category) === MILEAGE_CATEGORY
        ? (Number(sanitizeMiles(formValue.mileage_miles)) || null)
        : null,
    };

    if (isEditing) {
      await onSubmit?.(payload, { asDraft });
      return;
    }

    // Each file goes up with the receipt tagging it was given here, so the
    // answers are stored on the attachment rather than lost with this form.
    await onSubmit?.({
      payload: asDraft ? { ...payload, status: 'draft' } : payload,
      attachments: attachmentEntries.map((entry) => {
        const amount = Number(sanitizeAmount(entry.amountRaw));
        return {
          file: entry.file,
          isReceipt: entry.isReceipt,
          receiptAmount: entry.isReceipt && Number.isFinite(amount) && amount > 0 ? amount : null,
        };
      }),
    });
  }, [amountRaw, formValue, onSubmit, isEditing, attachmentEntries, canSubmitOnBehalf, selectedEmployeeId]);

  // New expenses have to pass through the receipt confirmation dialog first.
  // Edits/resubmits manage their files elsewhere, and technology
  // reimbursements are calculated rather than receipted, so both submit
  // directly. So does the All Hands meal, which needs no receipt at all.
  const requiresReceiptConfirmation = !isEditing && !isTechReimbursement && !isAllHandsMeal;

  // Both entry points below are wired straight to a click handler, so a
  // rejection here escapes as an unhandled promise rejection — which in
  // development puts the error overlay over the app, as if the form had
  // crashed. The parent has already reported the failure (toast, plus the
  // `error` prop this form renders) by the time it rethrows to say "not
  // submitted", so swallowing it at this boundary loses nothing.
  const runSubmit = useCallback(async (asDraft = false) => {
    try {
      await performSubmit(asDraft);
    } catch (err) {
      console.error('Expense submit failed', err);
    }
  }, [performSubmit]);

  const submit = useCallback(async () => {
    setValidationAttempted(true);
    if (!isFormValid) {
      return;
    }

    if (requiresReceiptConfirmation) {
      setReceiptConfirmed(false);
      setConfirmOpen(true);
      return;
    }

    await runSubmit();
  }, [isFormValid, requiresReceiptConfirmation, runSubmit]);

  // Saving without submitting. Everything the expense needs to be stored still
  // has to be there, but the receipt rules don't apply: not having the receipt
  // yet is the usual reason for saving instead of submitting, and there is
  // nothing to reconcile against a claim that hasn't been made.
  const save = useCallback(async () => {
    setValidationAttempted(true);
    if (!isDraftValid) return;
    await runSubmit(true);
  }, [isDraftValid, runSubmit]);

  const confirmAndSubmit = useCallback(async () => {
    if (!receiptConfirmed) return;
    try {
      await runSubmit();
    } finally {
      // Close on failure too, so the form's error message isn't hidden behind
      // the dialog. On success the parent closes the whole modal.
      setConfirmOpen(false);
    }
  }, [receiptConfirmed, runSubmit]);

  useEffect(() => {
    if (!submitRef) return;
    const actions = { submit, save };
    submitRef.current = actions;
    return () => {
      if (submitRef.current === actions) {
        submitRef.current = null;
      }
    };
  }, [submitRef, submit, save]);

  // Rendered in different positions depending on category: normally above the
  // purchase date, but for technology reimbursements it sits below the
  // reimbursement month range since the amount is derived from that range.
  const amountField = (
    <Form.Group controlId="amount">
      <Form.ControlLabel>
        Amount
        <RequiredAsterisk />
      </Form.ControlLabel>
      <Input
        value={amountDisplay}
        onChange={handleAmountChange}
        onBlur={handleAmountBlur}
        placeholder="$0.00"
        className={showFieldError('amount') ? 'is-invalid' : ''}
        disabled={isTechReimbursement || isAllHandsMeal || isMileage}
        required
      />
      {isTechReimbursement ? (
        <Form.HelpText>
          Calculated automatically: {techReimbursementMonths} month{techReimbursementMonths === 1 ? '' : 's'}
          {' '}× {formatCurrency(TECHNOLOGY_REIMBURSEMENT_MONTHLY_RATE)} based on the purchase date range.
        </Form.HelpText>
      ) : null}
      {isMileage ? (
        <Form.HelpText>
          {mileagePricing?.rate
            ? `Calculated automatically: ${mileagePricing.miles.toLocaleString('en-US', { maximumFractionDigits: 2 })} mi`
              + ` × ${formatMileageRate(mileagePricing.rate.rate_per_mile)} per mile, the rate in effect from `
              + `${formatIsoDate(mileagePricing.rate.effective_date)}.`
            : 'Calculated automatically from the miles driven and the rate in effect on the trip date.'}
        </Form.HelpText>
      ) : null}
      {isAllHandsMeal ? (
        <Form.HelpText>
          {allHandsMealQuery.isError
            ? "Couldn't load the pre-approved amount. Close the form and try again."
            : 'Fixed pre-approved amount for the All Hands Meeting meal.'}
        </Form.HelpText>
      ) : null}
      {showFieldError('amount') ? <div className="invalid-feedback d-block">{validationErrors.amount}</div> : null}
    </Form.Group>
  );

  // Sits under the trip date, since the rate comes from it, with the amount it
  // prices just below.
  const milesField = (
    <Form.Group controlId="mileage_miles">
      <Form.ControlLabel>
        Miles Driven
        <RequiredAsterisk />
      </Form.ControlLabel>
      <Input
        value={formValue.mileage_miles}
        onChange={(value) => setFormValue((prev) => ({ ...prev, mileage_miles: sanitizeMiles(value) }))}
        placeholder="0.0"
        inputMode="decimal"
        className={showFieldError('mileage_miles') ? 'is-invalid' : ''}
        required
      />
      <Form.HelpText>Total miles driven, matching the route screenshot you attach below.</Form.HelpText>
      {showFieldError('mileage_miles') ? <div className="invalid-feedback d-block">{validationErrors.mileage_miles}</div> : null}
      {/* Shown as soon as it applies, not only after a submit attempt: it
          explains why the amount is empty, and nothing typed here fixes it. */}
      {validationErrors.mileage_rate && !mileageRatesQuery.isLoading ? (
        <div className="invalid-feedback d-block">{validationErrors.mileage_rate}</div>
      ) : null}
    </Form.Group>
  );

  return (
    <div ref={rootRef} className="expense-form">
      {error ? (
        <Message type="error" showIcon header="Error" style={{ marginBottom: 12 }}>
          {String(error?.message || error)}
        </Message>
      ) : null}

      <Form fluid formValue={formValue} onChange={setFormValue}>
        <Panel
          bordered
          header={(
            <div className="d-flex justify-content-between align-items-center">
              <span>Expense Details</span>
              <span className="text-muted" style={{ fontWeight: 400, fontSize: 14 }}>
                Submission Date - {submissionDate}
              </span>
            </div>
          )}
        >
          {/* Not gated on the category: who the expense belongs to is worth
              showing (and choosing, when submitting on behalf of someone else)
              before anything else is answered. */}
          <Form.Group controlId="employee_name">
            <Form.ControlLabel>Employee Name</Form.ControlLabel>
            {canSubmitOnBehalf ? (
              <>
                <SelectPicker
                  data={employeeOptions}
                  value={selectedEmployeeId}
                  onChange={(value) => setOnBehalfEmployeeId(value)}
                  cleanable={false}
                  block
                  placeholder="Select employee"
                  loading={directoryQuery.isLoading}
                  disabled={Boolean(lockedEmployeeId)}
                  container={getPickerContainer}
                />
                <Form.HelpText>
                  {lockedEmployeeId
                    ? 'Set when this group was started. Every expense in a group belongs to the same employee.'
                    : 'You can submit this expense on behalf of another employee. It will be owned by the selected employee.'}
                </Form.HelpText>
                {isOnBehalfOfOther ? (
                  <Message type="warning" showIcon style={{ marginTop: 8 }}>
                    You are submitting this expense on behalf of someone else
                    {selectedEmployeeName ? ` (${selectedEmployeeName})` : ''}. It will be owned by them, not you.
                  </Message>
                ) : null}
              </>
            ) : (
              <div style={{ fontWeight: 500 }}>{employeeName}</div>
            )}
          </Form.Group>

          {!isTechReimbursement && !isMileage ? amountField : null}

          <Form.Group controlId="category">
            <Form.ControlLabel>
              Category
              <RequiredAsterisk />
            </Form.ControlLabel>
            <SelectPicker
              data={categoryOptions}
              value={formValue.category || null}
              placeholder="Select a category to continue"
              onChange={(value) =>
                setFormValue((prev) => {
                  const category = normalizeCategory(value);
                  if (category === TECHNOLOGY_REIMBURSEMENT_CATEGORY) {
                    // Amount is derived from a date range, so force the multiple
                    // purchase dates flow and clear the travel toggle. The vendor
                    // is always NSI for technology reimbursements.
                    return {
                      ...prev,
                      category,
                      travel: false,
                      travel_dates: null,
                      has_multiple_purchase_dates: true,
                      vendor_name: 'NSI',
                      mileage_miles: '',
                      purchased_by_me: false,
                      reason_for_purchase: '',
                      customer_reimbursable: false,
                      customer_name: '',
                    };
                  }
                  if (category === ALL_HANDS_MEAL_CATEGORY) {
                    // Only the meeting date is asked for. Clear every flag the
                    // hidden fields could have left set, so the form doesn't
                    // keep answers the submitter can no longer see.
                    return {
                      ...prev,
                      category,
                      travel: false,
                      travel_dates: null,
                      has_multiple_purchase_dates: false,
                      purchase_date_range: null,
                      vendor_name: '',
                      mileage_miles: '',
                      purchased_by_me: false,
                      customer_reimbursable: false,
                      customer_name: '',
                    };
                  }
                  // Vendor name is hidden for categories that don't use it, so
                  // drop any value carried over from a previous category to
                  // avoid submitting a vendor the user can no longer see. The
                  // same applies to travel: if the new category can't be a
                  // travel expense, clear the toggle so it isn't left set while
                  // the question is hidden.
                  const travelEligible = isTravelEligibleCategory(category);
                  // Airfare/lodging/meals/ground transportation are inherently
                  // travel, so force the toggle on (and mirror the travel
                  // toggle's own reset of the multiple-purchase-dates flow).
                  const travelRequired = isTravelRequiredCategory(category);
                  return {
                    ...prev,
                    category,
                    vendor_name: isVendorNameRequired(category) ? prev.vendor_name : '',
                    travel: travelRequired ? true : (travelEligible ? prev.travel : false),
                    travel_dates: travelEligible ? prev.travel_dates : null,
                    has_multiple_purchase_dates: travelRequired ? false : prev.has_multiple_purchase_dates,
                    purchase_date_range: travelRequired ? null : prev.purchase_date_range,
                    // Only mileage asks for miles; don't carry them into a
                    // category that can't show them.
                    mileage_miles: category === MILEAGE_CATEGORY ? prev.mileage_miles : '',
                  };
                })
              }
              cleanable={false}
              searchable={false}
              style={{ width: '100%', ...validationBorder('category') }}
              // Deliberately not using getPickerContainer. Before a category is
              // picked the form is only two fields tall, and an overlay
              // rendered inside the scrollable modal body gets clipped by it.
              // Portaling to document.body lets the menu overhang the modal
              // (.rs-picker-popup sits at z-index 1055, above the modal's
              // 1050). The trade is that it doesn't track the body's internal
              // scroll — harmless here, since this field is at the top of the
              // form and there is nothing to scroll until it's answered.
              placement="bottomStart"
              preventOverflow
            />
            {showFieldError('category') ? <div className="invalid-feedback d-block">{validationErrors.category}</div> : null}
          </Form.Group>

          {/* The rest of the form depends on the category, so it only appears
              once one is chosen. */}
          {hasCategory ? (
          <>

          {/* Boolean options that apply to the expense, grouped as a 2x2 grid of
              toggles. Each cell keeps its own visibility rules, so the grid
              reflows when an option doesn't apply to the current category. */}
          {isAllHandsMeal ? (
            <Message type="info" showIcon style={{ marginBottom: 16 }}>
              This meal is pre-approved. No receipt is needed: pick the meeting date and
              submit, and it goes straight to your approved expenses for payroll to pay.
            </Message>
          ) : null}

          {!isTechReimbursement && !isAllHandsMeal ? (
            <Form.Group controlId="expense_flags">
              <div className="expense-toggle-grid">
                <div className={`expense-toggle${formValue.travel ? ' expense-toggle--disabled' : ''}`}>
                  <Toggle
                    checked={!formValue.travel && formValue.has_multiple_purchase_dates === true}
                    disabled={formValue.travel}
                    onChange={(checked) =>
                      setFormValue((prev) => ({
                        ...prev,
                        has_multiple_purchase_dates: checked,
                        purchase_date_range: checked ? prev.purchase_date_range : null,
                      }))
                    }
                  />
                  <span>Are there multiple purchase dates?</span>
                </div>

                <div className="expense-toggle">
                  <Toggle
                    checked={!!formValue.purchased_by_me}
                    onChange={(checked) => setFormValue((prev) => ({ ...prev, purchased_by_me: checked }))}
                  />
                  <span>Was the company card used?</span>
                </div>

                <div className="expense-toggle">
                  <Toggle
                    checked={!!formValue.customer_reimbursable}
                    onChange={(checked) =>
                      setFormValue((prev) => ({
                        ...prev,
                        customer_reimbursable: checked,
                        customer_name: checked ? prev.customer_name : '',
                      }))
                    }
                  />
                  <span>Customer Reimbursable?</span>
                </div>

                {isTravelEligibleCategory(formValue.category) ? (
                  <div className={`expense-toggle${isTravelRequiredCategory(formValue.category) ? ' expense-toggle--disabled' : ''}`}>
                    <Toggle
                      checked={isTravelRequiredCategory(formValue.category) || !!formValue.travel}
                      disabled={isTravelRequiredCategory(formValue.category)}
                      onChange={(checked) =>
                        setFormValue((prev) => ({
                          ...prev,
                          travel: checked,
                          travel_dates: checked ? prev.travel_dates : null,
                          has_multiple_purchase_dates: checked ? false : prev.has_multiple_purchase_dates,
                          purchase_date_range: checked ? null : prev.purchase_date_range,
                        }))
                      }
                    />
                    <span>Travel?</span>
                  </div>
                ) : null}
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--dm-line-red, #f5c6c2)',
                  background: 'var(--dm-tint-red, #fef3f2)',
                  color: 'var(--dm-text-red, #b42318)',
                  fontWeight: 600,
                }}
              >
                You must upload the receipt for anything reimbursable. Credit card or bank
                statements are not acceptable, only the receipt.
              </div>
            </Form.Group>
          ) : null}

          {isTechReimbursement ? (
            <Form.Group controlId="purchase_date_range">
              <Form.ControlLabel>
                Reimbursement Month Range
                <RequiredAsterisk />
              </Form.ControlLabel>
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  value={toDate(formValue.purchase_date_range?.[0])}
                  onChange={(value) =>
                    setFormValue((prev) => ({
                      ...prev,
                      purchase_date_range: [startOfMonth(value), prev.purchase_date_range?.[1] || null],
                    }))
                  }
                  format="MM/yyyy"
                  style={{ flex: 1, ...validationBorder('purchase_date_range') }}
                  placeholder="From month"
                  oneTap
                  cleanable={false}
                  container={getPickerContainer}
                />
                <span className="text-muted">to</span>
                <DatePicker
                  value={toDate(formValue.purchase_date_range?.[1])}
                  onChange={(value) =>
                    setFormValue((prev) => ({
                      ...prev,
                      purchase_date_range: [prev.purchase_date_range?.[0] || null, endOfMonth(value)],
                    }))
                  }
                  format="MM/yyyy"
                  style={{ flex: 1, ...validationBorder('purchase_date_range') }}
                  placeholder="To month"
                  oneTap
                  cleanable={false}
                  container={getPickerContainer}
                />
              </div>
              {showFieldError('purchase_date_range') ? <div className="invalid-feedback d-block">{validationErrors.purchase_date_range}</div> : null}
            </Form.Group>
          ) : (!formValue.travel && formValue.has_multiple_purchase_dates) ? (
            <Form.Group controlId="purchase_date_range">
              <Form.ControlLabel>
                Purchase Date Range
                <RequiredAsterisk />
              </Form.ControlLabel>
              <DateRangePicker
                value={formValue.purchase_date_range}
                onChange={(value) => setFormValue((prev) => ({ ...prev, purchase_date_range: value }))}
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                style={{ width: '100%', ...validationBorder('purchase_date_range') }}
                placeholder="Select purchase date range"
                container={getPickerContainer}
              />
              {showFieldError('purchase_date_range') ? <div className="invalid-feedback d-block">{validationErrors.purchase_date_range}</div> : null}
            </Form.Group>
          ) : (
            <Form.Group controlId="purchase_date">
              <Form.ControlLabel>
                {isAllHandsMeal ? 'Meeting Date' : 'Purchase Date'}
                <RequiredAsterisk />
              </Form.ControlLabel>
              <DatePicker
                value={formValue.purchase_date}
                onChange={(value) => setFormValue((prev) => ({ ...prev, purchase_date: value }))}
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                style={{ width: '100%', ...validationBorder('purchase_date') }}
                oneTap
                container={getPickerContainer}
              />
              {showFieldError('purchase_date') ? <div className="invalid-feedback d-block">{validationErrors.purchase_date}</div> : null}
            </Form.Group>
          )}

          {isMileage ? milesField : null}
          {isTechReimbursement || isMileage ? amountField : null}

          {formValue.travel ? (
            <Form.Group controlId="travel_dates">
              <Form.ControlLabel>
                Travel Dates
                <RequiredAsterisk />
              </Form.ControlLabel>
              <DateRangePicker
                ref={travelDatesRef}
                value={formValue.travel_dates}
                onChange={(value) => setFormValue((prev) => ({ ...prev, travel_dates: value }))}
                onSelect={handleTravelDateSelect}
                onEnter={resetTravelDateSelection}
                onExit={resetTravelDateSelection}
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                style={{ width: '100%', ...validationBorder('travel_dates') }}
                placeholder="Select travel date range"
                container={getPickerContainer}
              />
              {showFieldError('travel_dates') ? <div className="invalid-feedback d-block">{validationErrors.travel_dates}</div> : null}
            </Form.Group>
          ) : null}

          {isVendorNameRequired(formValue.category) ? (
          <Form.Group controlId="vendor_name">
            <Form.ControlLabel>
              Vendor Name (Type to create new)
              <RequiredAsterisk />
            </Form.ControlLabel>
            <CreatableSelect
              value={selectedVendor}
              onChange={(option) => setFormValue((prev) => ({ ...prev, vendor_name: option?.value || '' }))}
              options={vendorOptions}
              isClearable
              placeholder="Select or create vendor"
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition="fixed"
              styles={{
                ...creatableSelectStyles,
                control: (base) => ({
                  ...base,
                  borderColor: showFieldError('vendor_name') ? 'var(--bs-danger)' : base.borderColor,
                  boxShadow: showFieldError('vendor_name') ? '0 0 0 0.2rem rgba(220,53,69,.25)' : base.boxShadow,
                }),
              }}
            />
            {showFieldError('vendor_name') ? <div className="invalid-feedback d-block">{validationErrors.vendor_name}</div> : null}
          </Form.Group>
          ) : null}

          {!isAllHandsMeal ? (
          <>
          <Form.Group controlId="description">
            <Form.ControlLabel>
              Description
              <RequiredAsterisk />
            </Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={formValue.description}
              onChange={(value) => setFormValue((prev) => ({ ...prev, description: value }))}
              placeholder={
                isTechReimbursement
                  ? 'List whether you are paying for internet, phone, or both'
                  : 'Describe what was purchased. e.g. Dinner for 4 days'
              }
              className={showFieldError('description') ? 'is-invalid' : ''}
              required
            />
            {showFieldError('description') ? <div className="invalid-feedback d-block">{validationErrors.description}</div> : null}
          </Form.Group>

          {!isTechReimbursement ? (
          <Form.Group controlId="reason_for_purchase">
            <Form.ControlLabel>
              Reason for Purchase
              <RequiredAsterisk />
            </Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={formValue.reason_for_purchase}
              onChange={(value) => setFormValue((prev) => ({ ...prev, reason_for_purchase: value }))}
              placeholder="Why did you need to make this purchase? e.g. Traveling to Seattle for onsite request from customer"
              className={showFieldError('reason_for_purchase') ? 'is-invalid' : ''}
              required

            />
            {showFieldError('reason_for_purchase') ? <div className="invalid-feedback d-block">{validationErrors.reason_for_purchase}</div> : null}
          </Form.Group>
          ) : null}

          {formValue.customer_reimbursable ? (
            <Form.Group controlId="customer_name">
              <Form.ControlLabel>
                Customer (Type to create new)
                <RequiredAsterisk />
              </Form.ControlLabel>
              <CreatableSelect
                value={selectedCustomer}
                onChange={(option) => setFormValue((prev) => ({ ...prev, customer_name: option?.value || '' }))}
                options={customerOptions}
                isClearable
                placeholder="Select or create customer"
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={{
                  ...creatableSelectStyles,
                  control: (base) => ({
                    ...base,
                    borderColor: showFieldError('customer_name') ? 'var(--bs-danger)' : base.borderColor,
                    boxShadow: showFieldError('customer_name') ? '0 0 0 0.2rem rgba(220,53,69,.25)' : base.boxShadow,
                  }),
                }}
              />
              {showFieldError('customer_name') ? <div className="invalid-feedback d-block">{validationErrors.customer_name}</div> : null}
            </Form.Group>
          ) : null}

          {!isTechReimbursement ? (
          <Form.Group controlId="attachments">
            <Form.ControlLabel>Attachments</Form.ControlLabel>
            {isMileage ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--dm-line-teal, #a9cfd2)',
                  background: 'var(--dm-surface-sunk, #e2f0f1)',
                  color: 'var(--dm-text-teal, #0d5c63)',
                  fontWeight: 600,
                }}
              >
                For mileage reimbursement, take a screenshot of your route map (e.g. Google
                Maps) showing the distance and upload it here.
              </div>
            ) : null}
            {isEditing ? (
              <>
                <ExpenseAttachmentsPanel
                  expenseId={initialValue?.id}
                  isMobile={isMobileViewport}
                  disabled={loading}
                  renderRowFooter={(row) => {
                    const entry = attachmentEntries.find((item) => item.key === String(row.id));
                    if (!entry) return null;
                    return (
                      <ReceiptTagFields
                        isReceipt={entry.isReceipt}
                        amountDisplay={entry.amountDisplay}
                        disabled={loading}
                        onToggle={(checked) => {
                          setAttachmentIsReceipt(entry.key, checked);
                          // The toggle clears any amount with it, so the write
                          // matches: no amount without a receipt.
                          persistReceiptTagging(entry.key, { isReceipt: checked, amountRaw: '' });
                        }}
                        onAmountChange={(value) => setAttachmentAmount(entry.key, value)}
                        // Written on blur rather than per keystroke: "12.50"
                        // passes through 1, 12, 12.5 on the way in, and none of
                        // those are worth storing.
                        onAmountBlur={() => {
                          formatAttachmentAmount(entry.key);
                          persistReceiptTagging(entry.key, {
                            isReceipt: entry.isReceipt,
                            amountRaw: entry.amountRaw,
                          });
                        }}
                      />
                    );
                  }}
                />
                {receiptTotals}
              </>
            ) : (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Upload attachments"
                  className="d-flex flex-column align-items-center justify-content-center text-center"
                  onClick={openFilePicker}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openFilePicker();
                    }
                  }}
                  onDragEnter={onUploadDragOver}
                  onDragOver={onUploadDragOver}
                  onDragLeave={onUploadDragLeave}
                  onDrop={onUploadDrop}
                  style={{
                    minHeight: 140,
                    borderRadius: 10,
                    border: `2px dashed ${isDragOverUpload ? 'var(--bdc-primary)' : 'var(--rs-border-primary)'}`,
                    background: isDragOverUpload ? 'var(--bdc-focus-ring)' : 'transparent',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    padding: 16,
                  }}
                >
                  <i
                    className="bi bi-cloud-arrow-up"
                    aria-hidden="true"
                    style={{ fontSize: 28, marginBottom: 8, color: 'var(--bdc-primary)' }}
                  />
                  <div style={{ fontWeight: 600 }}>
                    {isMobileViewport ? 'Tap below to add files or take a photo' : 'Drag and drop files here'}
                  </div>
                  <div className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>or</div>
                  <div className="d-flex gap-2 flex-wrap justify-content-center">
                    <Button appearance="primary" size="sm" onClick={(event) => { event.stopPropagation(); openFilePicker(); }}>
                      <i className="bi bi-plus-lg me-2" />
                      Add files
                    </Button>
                    {isMobileViewport ? (
                      <Button appearance="ghost" size="sm" onClick={(event) => { event.stopPropagation(); openCameraPicker(); }}>
                        <i className="bi bi-camera me-2" />
                        Take photo
                      </Button>
                    ) : null}
                  </div>

                  <div className="text-muted" style={{ fontSize: 12, marginTop: 10 }}>
                    Accepted: {ALLOWED_ATTACHMENT_SUMMARY}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    disabled={loading}
                    style={{ display: 'none' }}
                    onChange={onUploadInputChange}
                  />

                  {isMobileViewport ? (
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      disabled={loading}
                      style={{ display: 'none' }}
                      onChange={onCameraInputChange}
                    />
                  ) : null}
                </div>

                {unsupportedFileError ? (
                  <div className="mt-2" style={{ fontSize: 13, color: 'var(--bs-danger)', fontWeight: 600 }}>
                    {unsupportedFileError}
                  </div>
                ) : null}

                {(attachments || []).length === 0 ? (
                  <div
                    className="mt-2"
                    style={{ fontSize: 13, color: showFieldError('attachments') ? 'var(--bs-danger)' : 'var(--rs-text-secondary)' }}
                  >
                    {showFieldError('attachments') ? validationErrors.attachments : 'No files selected.'}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-1 mt-2">
                    {attachmentEntries.map((entry, index) => (
                      <div
                        key={entry.key}
                        style={{
                          border: '1px solid var(--rs-border-secondary)',
                          borderRadius: 8,
                          padding: '8px 10px',
                          background: 'var(--rs-bg-card)'
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="text-truncate me-2" style={{ maxWidth: '75%' }} title={entry.file.name}>
                            {entry.file.name}
                          </div>
                          <Button appearance="subtle" size="xs" color="red" onClick={() => removeAttachment(index)}>
                            Remove
                          </Button>
                        </div>

                        <div className="mt-2">
                          <ReceiptTagFields
                            isReceipt={entry.isReceipt}
                            amountDisplay={entry.amountDisplay}
                            onToggle={(checked) => setAttachmentIsReceipt(entry.key, checked)}
                            onAmountChange={(value) => setAttachmentAmount(entry.key, value)}
                            onAmountBlur={() => formatAttachmentAmount(entry.key)}
                          />
                        </div>
                      </div>
                    ))}

                    {receiptTotals}
                  </div>
                )}
              </>
            )}
          </Form.Group>
          ) : null}
          </>
          ) : null}

          </>
          ) : null}

          {showActions ? (
            <div className="d-flex justify-content-end mt-3">
              <ButtonGroup>
                <Button appearance="subtle" onClick={onCancel}>{cancelLabel}</Button>
                {/* No drafts of the All Hands meal: it is approved on submit. */}
                {showSaveAction && !isAllHandsMeal ? (
                  <Button
                    appearance="ghost"
                    onClick={save}
                    loading={loading}
                    disabled={!isDraftValid || loading}
                  >
                    {saveLabel}
                  </Button>
                ) : null}
                {!isFormValid && !loading ? (
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={(
                      <Tooltip>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Complete the following to submit:</div>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {Object.values(validationErrors).map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      </Tooltip>
                    )}
                  >
                    {/* A disabled button suppresses hover events, so the wrapper
                        span is the Whisper trigger and the button's own pointer
                        events are turned off. */}
                    <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                      <Button appearance="primary" disabled style={{ pointerEvents: 'none' }}>{submitLabel}</Button>
                    </span>
                  </Whisper>
                ) : (
                  <Button appearance="primary" onClick={submit} loading={loading} disabled={!isFormValid || loading}>{submitLabel}</Button>
                )}
              </ButtonGroup>
            </div>
          ) : null}
        </Panel>
      </Form>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (loading) return;
          setConfirmOpen(false);
        }}
        size="xs"
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Confirm Receipts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Checkbox
            checked={receiptConfirmed}
            onChange={(_value, checked) => setReceiptConfirmed(checked)}
            disabled={loading}
          >
            I confirm that I have attached a receipt for <b>every individual item</b> and
            that the amount entered is the sum of all of these receipts exactly
          </Checkbox>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={confirmAndSubmit}
            loading={loading}
            disabled={!receiptConfirmed || loading}
          >
            {submitLabel}
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
